import styled, { css } from 'styled-components';
import IonTable from '../IonTable';
import NumberInput from '../Input/NumberInput';
import {
    roundDecimal,
    roundSignificant,
    decimalRoundSignificant,
} from './utils';
import PrintButton from './PrintButton';
import { fertilizerTypes } from '../../services/fertilizer';
import useStore from '../../store/useStore';

const Flexbox = styled.div`
    display: flex;
    gap: 32px;
    position: relative;
`;

const FormattedNumberInput = styled(NumberInput).attrs({
    inputWidth: '100%',
})`
    background: '#eee';
`;

const unitOrderWeight = {
    mg: 1000000,
    g: 1000,
    kg: 1,
    'ml/l': 1,
    'l/l': 0.001,
    'cl/l': 0.1,
    'dl/l': 0.01,
};

const OutputPanelFertilizer = ({
    ionTableColumns,
    ionParameters,
    fertilizerData,
    balancedFertilizerData,
}) => {
    const { store } = useStore();
    const fertilizerSettings = store.input.fertilizerSettings;
    const fertilizerUnits = fertilizerData?.fertilizer_units;
    const acid = fertilizerData?.acid;
    const base = fertilizerData?.base;

    const fertilizerType = fertilizerSettings?.fertilizerType;
    const abTanks =
        [
            fertilizerTypes.solid,
            fertilizerTypes.solidWithLiquidCalcium,
        ].includes(fertilizerType) && fertilizerSettings?.abTanks;
    const showBalancedData = Boolean(balancedFertilizerData);

    let fertilizerRows;
    let acidRows;

    const decimals = 3;

    const getFertilizerTableColumns = (tank) => {
        const columns = [
            {
                label: tank ? `Tank ${tank}` : '',
                width: 180,
                cellStyle: {
                    textAlign: 'left',
                },
                formatter: (row, r, c) => (
                    <b>{Array.isArray(row) ? row[c] : row.data?.[c]}</b>
                ),
            },
            {
                label: 'meststof',
                width: 100,
                cellStyle: {
                    textAlign: 'right',
                },
                formatter: (row, r, c) => {
                    const rowArray = Array.isArray(row) ? row : row.data;
                    const d = rowArray[c];
                    if (!d) {
                        return '-';
                    }

                    return (
                        <FormattedNumberInput
                            value={decimalRoundSignificant(d, decimals)}
                            // value={roundDecimal(d, decimals)}
                            disabled
                            output
                        />
                    );
                },
            },
        ];
        if (showBalancedData) {
            columns.push({
                label: 'gebalanceerd',
                width: 100,
                cellStyle: {
                    textAlign: 'right',
                },
                formatter: (row, r, c) => {
                    const rowArray = Array.isArray(row) ? row : row.data;
                    const d = rowArray[c];
                    if (!d) {
                        return '-';
                    }

                    return (
                        <FormattedNumberInput
                            value={decimalRoundSignificant(d, decimals)}
                            // value={roundDecimal(d, decimals)}
                            disabled
                            output
                        />
                    );
                },
            });
        }
        columns.push({
            label: 'unit',
            width: 50,
            formatter: (row, r, c) =>
                Array.isArray(row) ? row[c] : row.data?.[c],
        });
        return columns;
    };

    const acidCheckTableColumns = [
        {
            label: 'Controle',
            width: 180,
            cellStyle: {
                textAlign: 'left',
            },
            formatter: (row, r, c) => <b>{row[c]}</b>,
        },
        {
            label: '',
            width: 100,
            cellStyle: {
                textAlign: 'right',
            },
            formatter: (row, r, c) => {
                const rowArray = Array.isArray(row) ? row : row.data;
                const d = rowArray[c];

                let warn = false;
                if (rowArray[0] === 'Verschil' && Math.abs(d) > 0.001) {
                    warn = true;
                }
                return (
                    <FormattedNumberInput
                        value={roundDecimal(d, 3)}
                        disabled
                        output
                        warn={warn}
                    />
                );
            },
        },
    ];
    if (showBalancedData) {
        acidCheckTableColumns.push({
            label: 'gebalanceerd',
            width: 100,
            cellStyle: {
                textAlign: 'right',
            },
            formatter: (row, r, c) => {
                const rowArray = Array.isArray(row) ? row : row.data;
                const d = rowArray[c];

                let warn = false;
                if (rowArray[0] === 'Verschil' && Math.abs(d) > 0.001) {
                    warn = true;
                }
                return (
                    <FormattedNumberInput
                        value={roundDecimal(d, 3)}
                        disabled
                        output
                        warn={warn}
                    />
                );
            },
        });
    }
    acidCheckTableColumns.push({ label: '', width: 50 });

    if (fertilizerUnits) {
        acidRows = [
            ['Zuur', acid],
            ['Loog', base],
            ['Verschil', acid - base],
        ];
        if (showBalancedData) {
            acidRows[0].push(balancedFertilizerData?.acid);
            acidRows[1].push(balancedFertilizerData?.base);
            acidRows[2].push(
                balancedFertilizerData?.acid - balancedFertilizerData?.base
            );
        }

        const balancedFertilizerValues =
            balancedFertilizerData?.fertilizer_units ??
            balancedFertilizerData?.applied_fertilizers;
        const orderParam = Object.values(fertilizerUnits).some((d) => d.order);
        fertilizerRows = Object.entries(fertilizerUnits)
            .toSorted((a, b) => {
                const [idA, contentA] = a;
                const [idB, contentB] = b;

                if (orderParam) {
                    return (contentA.order ?? 0) - (contentB.order ?? 0);
                }

                return (
                    (unitOrderWeight[contentA.units] ?? -1) -
                    (unitOrderWeight[contentB.units] ?? -1)
                );
            })
            .map(([fertilizerId, content]) => {
                if (showBalancedData) {
                    console.log(
                        'balancedFertilizerData:',
                        balancedFertilizerData
                    );

                    const balancedValue =
                        balancedFertilizerValues?.[fertilizerId]?.value;
                    return {
                        data: [
                            fertilizerId,
                            content.value,
                            balancedValue,
                            content.units,
                        ],
                        tank: content.tank,
                    };
                }
                return {
                    data: [fertilizerId, content.value, content.units],
                    tank: content.tank,
                };
            });
    }

    return (
        <div>
            <PrintButton />
            <Flexbox>
                {fertilizerRows && (
                    <div>
                        {abTanks ? (
                            <>
                                <IonTable
                                    columns={getFertilizerTableColumns('A')}
                                    data={fertilizerRows.filter(
                                        (d) => d.tank?.toUpperCase() === 'A'
                                    )}
                                    style={{ marginBottom: 8 }}
                                />
                                <IonTable
                                    columns={getFertilizerTableColumns('B')}
                                    data={fertilizerRows.filter(
                                        (d) => d.tank.toUpperCase() === 'B'
                                    )}
                                />
                            </>
                        ) : (
                            <IonTable
                                columns={getFertilizerTableColumns()}
                                data={fertilizerRows}
                            />
                        )}
                        <IonTable
                            columns={acidCheckTableColumns}
                            data={acidRows}
                            style={{ marginTop: 32, width: '100%' }}
                        />
                    </div>
                )}
                <IonTable columns={ionTableColumns} data={ionParameters} />
            </Flexbox>
        </div>
    );
};

export default OutputPanelFertilizer;
